import { Component, OnInit, ViewChild, signal, input } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { dataStore, db } from 'src/app/app.component';
import { Vehicle } from 'src/app/interfaces';
import {
  collection,
  getCountFromServer,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';
import { ucFirst } from 'src/app/globals';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  EditRidePreparationItemDialogComponent,
  Field,
} from '../../../ride-preparation/dialogs/edit-ride-preparation-item-dialog/edit-ride-preparation-item-dialog.component';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { NgStyle, NgClass } from '@angular/common';
import { MatRipple } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Role } from 'src/app/enums';

@Component({
  selector: 'app-vehicles',
  templateUrl: './vehicles.component.html',
  styleUrl: './vehicles.component.scss',
  imports: [
    MatCard,
    MatFormField,
    MatIcon,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatTooltip,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    NgStyle,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatRipple,
    NgClass,
    MatProgressSpinner,
  ],
})
export class VehiclesComponent implements OnInit {
  usesPrivateVehicles = !!dataStore.paidFunctionalities?.usesPrivateVehicles;
  parentData = input<{ processing: boolean }>(undefined);
  @ViewChild('table', { static: false }) table: MatTable<any>;
  vehicles = signal<Vehicle[]>([]);
  filteredVehicles = signal<Vehicle[]>([]);
  displayedColumns = [
    'name',
    ...(dataStore.paidFunctionalities?.usesPrivateVehicles ? ['vehicleType'] : []),
    'seats',
    'sortingNumber',
    'currentMileage',
    'color',
    'backgroundColorEmpty',
    'backgroundColorFull',
    'backgroundColorNoDriver',
    'button',
  ];
  displayedColumnsWithout = [
    'name',
    'seats',
    'sortingNumber',
    'currentMileage',
    'color',
    'backgroundColorEmpty',
    'backgroundColorFull',
    'button',
  ];
  totalVehicles = signal<number>(0);
  totalActive = signal<number>(0);
  header: boolean = true;
  fileUploaded: File;
  orderNumbersArray = signal<number[]>([]);
  ignoreDriverStatus: boolean = dataStore.generalSettings?.ignoreDriverStatus ?? true;
  showDriverPlanning: boolean = dataStore.paidFunctionalities?.usesDriverPlanning ?? false;
  searchCtrl = new UntypedFormControl();
  showInactive = signal(false);
  organisationId = dataStore.selectedOrganisation.id;

  constructor(
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    if (!this.showDriverPlanning || this.ignoreDriverStatus) {
      this.displayedColumns.splice(this.displayedColumns.indexOf('backgroundColorNoDriver'), 1);
    }
    onSnapshot(
      query(
        collection(db, `organisations/${this.organisationId}/vehicles`),
        orderBy('calendarStyle.sortingNumber'),
      ),
      (snapshot) => {
        const vehicles: Vehicle[] = [];
        const orderNumbers: number[] = [];
        this.totalVehicles.set(snapshot.size);
        this.totalActive.set(0);
        snapshot.forEach((doc) => {
          const data = doc.data() as Vehicle;
          const id = doc.id;
          data.name = ucFirst(data?.name ?? '');
          vehicles.push({ id, ...data });
          if (
            data.calendarStyle?.sortingNumber !== null &&
            data.calendarStyle?.sortingNumber !== undefined
          ) {
            orderNumbers.push(Number(data.calendarStyle.sortingNumber));
          }

          if (data.active) {
            this.totalActive.update((count) => count + 1);
          }
        });
        this.vehicles.set(vehicles);
        this.orderNumbersArray.set(orderNumbers);
        this.updateFilteredVehicles(vehicles);
      },
    );
    this.searchCtrl.valueChanges.subscribe((value) => {
      this.updateFilteredVehicles(this.vehicles(), value);
    });
  }

  updateFilteredVehicles(
    vehicles: Vehicle[] = this.vehicles(),
    searchValue = this.searchCtrl.value,
  ) {
    this.filteredVehicles.set(this.filterVehicles(searchValue, vehicles));
  }

  filterVehicles(value: string, array: Vehicle[]): Vehicle[] {
    const filterValue = String(value ?? '').toLowerCase();
    return array.filter((vehicle) => {
      const isActive = vehicle.active === true;
      const matchesStatus = this.showInactive() ? !isActive : isActive;
      const matchesSearch =
        !filterValue ||
        String(vehicle.name ?? '')
          .toLowerCase()
          .includes(filterValue);

      return matchesStatus && matchesSearch;
    });
  }

  toggleStatusView() {
    this.showInactive.update((showInactive) => !showInactive);
    this.updateFilteredVehicles();
  }

  clearSearch() {
    this.searchCtrl.setValue('');
    this.searchCtrl.updateValueAndValidity();
  }

  async editItem(vehicle?: Vehicle) {
    let driverOptions = [];
    let coDriverOptions = [];
    if (dataStore.paidFunctionalities?.usesPrivateVehicles) {
      const usersCollection = collection(db, `organisations/${this.organisationId}/users`);
      try {
        const [drivers, coDrivers] = await Promise.all([
          getDocs(query(usersCollection, where('roles', 'array-contains', Role.Driver))),
          dataStore.generalSettings.coDriverEnabled
            ? getDocs(query(usersCollection, where('roles', 'array-contains', Role.CoDriver)))
            : Promise.resolve({ docs: [] }),
        ]);
        driverOptions = drivers.docs.map((userDoc) => ({
          id: userDoc.id,
          name: userDoc.data().name ?? userDoc.data().lastName ?? userDoc.id,
        }));
        const assignedDriverIds = new Set(
          this.vehicles()
            .filter(
              (candidate) => candidate.vehicleType === 'private' && candidate.id !== vehicle?.id,
            )
            .map((candidate) => candidate.driverId)
            .filter((driverId): driverId is string => !!driverId),
        );
        driverOptions = driverOptions.filter(
          (driverOption) => !assignedDriverIds.has(driverOption.id),
        );
        coDriverOptions = coDrivers.docs.map((userDoc) => ({
          id: userDoc.id,
          name: userDoc.data().name ?? userDoc.data().lastName ?? userDoc.id,
        }));
      } catch (error) {
        console.error('Could not load vehicle crew options', error);
        this.snackBar.open('Chauffeurs konden niet worden geladen. Probeer het opnieuw.', 'X', {
          duration: 5000,
        });
        return;
      }
    }
    const fields: Field[] = [
      {
        key: 'active',
        type: 'toggle',
        label: 'Actief',
        required: false,
        row: 'status',
        disabled: vehicle?.active
          ? false
          : this.totalActive() >= dataStore.generalSettings?.maxActiveVehicles,
      },
      {
        key: 'name',
        type: 'string',
        label: 'Voertuignaam',
        required: true,
        row: 'identity',
      },
      {
        key: 'description',
        type: 'string',
        label: 'Omschrijving',
        row: 'identity',
      },
      {
        key: 'sortingNumber',
        type: 'order',
        label: 'Volgordenummer',
        required: true,
        row: 'identity',
      },
      {
        key: 'seats',
        type: 'numberInput',
        label: 'Aantal zitplaatsen',
        required: true,
        min: 1,
        row: 'vehicle-details',
      },
      dataStore.paidFunctionalities?.usesPrivateVehicles
        ? {
            key: 'vehicleType',
            type: 'select',
            label: 'Voertuigtype',
            group: [
              { id: 'organisation', name: 'Organisatie' },
              { id: 'private', name: 'Privé' },
            ],
            row: 'vehicle-details',
          }
        : null,
      dataStore.paidFunctionalities?.usesPrivateVehicles
        ? {
            key: 'driverId',
            type: 'select',
            label: 'Vaste chauffeur',
            group: driverOptions,
            required: true,
            searchable: true,
            showWhen: { field: 'vehicleType', value: 'private' },
            row: 'vehicle-details',
          }
        : null,
      dataStore.paidFunctionalities?.usesPrivateVehicles &&
      dataStore.generalSettings.coDriverEnabled
        ? {
            key: 'coDriverId',
            type: 'select',
            label: 'Vaste bijrijder (optioneel)',
            group: coDriverOptions,
            searchable: true,
            showWhen: { field: 'vehicleType', value: 'private' },
            row: 'vehicle-details',
          }
        : null,
      {
        key: 'color',
        type: 'color',
        label: 'Auto kleur',
        row: 'colors',
      },
      {
        key: 'backgroundColorEmpty',
        type: 'color',
        label: 'Rit leeg kleur',
        required: false,
        row: 'colors',
      },
      {
        key: 'backgroundColorFull',
        type: 'color',
        label: 'Rit vol kleur',
        required: false,
        row: 'colors',
      },
      this.showDriverPlanning && !this.ignoreDriverStatus
        ? {
            key: 'backgroundColorNoDriver',
            type: 'color',
            label: 'Geen chauffeur kleur',
            required: false,
            row: 'colors',
          }
        : null,
      {
        key: 'particularityIds',
        type: 'iconGroup',
        label: 'Bijzonderheden',
        group: dataStore.selectedOrganisation.particularities,
        required: false,
        row: 'particularities',
      },
    ];
    const dialogRef = this.dialog.open(EditRidePreparationItemDialogComponent, {
      width: '1000px',
      autoFocus: false,
      data: {
        item: vehicle,
        itemLabel: 'voertuig',
        fields: fields,
        optionalData: {
          orderNumbersArray: this.orderNumbersArray(),
          totalActive: this.totalActive(),
        },
        beforeSave: async (saveObj) => {
          if (!vehicle?.active || saveObj['active'] !== false) {
            return true;
          }

          return this.confirmVehicleDeactivation(vehicle.id);
        },
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && vehicle && vehicle.active && !result.active) {
        this.removeVehicleFromDayparts(vehicle.id);
        this.removeVehicleFromVehicleGroups(vehicle);
      } else if (result && vehicle && !vehicle.active && result.active) {
        this.addVehicleToDayparts(vehicle.id);
      }
    });
  }

  private async confirmVehicleDeactivation(vehicleId: string): Promise<boolean> {
    const deactivationTime = new Date();
    const ridesQuery = query(
      collection(db, `organisations/${this.organisationId}/rides`),
      where('vehicleId', '==', vehicleId),
      where('start', '>', deactivationTime),
    );

    try {
      const countSnapshot = await getCountFromServer(ridesQuery);
      const affectedRideCount = countSnapshot.data().count;
      const includeDriver = this.showDriverPlanning;
      const includeCoDriver = includeDriver && dataStore.generalSettings.coDriverEnabled;
      const dialogRef = this.dialog.open(WarningDialog, {
        width: '515px',
        autoFocus: false,
        data: {
          dialogType: 'deactivateVehicle',
          optionalData: {
            affectedRideCount,
            includeDriver,
            includeCoDriver,
          },
        },
      });

      return (await firstValueFrom(dialogRef.afterClosed())) === true;
    } catch (error) {
      console.error('Could not count future rides for vehicle deactivation', error);
      this.snackBar.open(
        'Toekomstige ritten konden niet worden geteld. Het voertuig blijft actief.',
        'X',
        { duration: 5000 },
      );
      return false;
    }
  }

  async addVehicleToDayparts(vehicleId: string) {
    const daypartDocs = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/dayparts`),
          where('type', '==', 'driver'),
        ),
      )
    ).docs;
    for (const daypartDoc of daypartDocs) {
      const data = daypartDoc.data();
      if (!data.vehicles) {
        data.vehicles = [];
      }
      if (!data.vehicles.includes(vehicleId)) {
        data.vehicles.push(vehicleId);
        await updateDoc(daypartDoc.ref, {
          vehicles: data.vehicles,
        });
      }
    }
  }

  async removeVehicleFromDayparts(vehicleId: string) {
    const daypartDocs = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/dayparts`),
          where('vehicles', 'array-contains', vehicleId),
        ),
      )
    ).docs;
    for (const daypartDoc of daypartDocs) {
      await updateDoc(daypartDoc.ref, {
        vehicles: daypartDoc.data().vehicles.filter((id: string) => id !== vehicleId),
      });
    }
  }

  async removeVehicleFromVehicleGroups(vehicle: Vehicle) {
    const vehicleGroupDocs = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId}/vehicleGroups`),
          where('vehicles', 'array-contains', vehicle.id),
        ),
      )
    ).docs;
    for (const vehicleGroupDoc of vehicleGroupDocs) {
      await updateDoc(vehicleGroupDoc.ref, {
        vehicles: vehicleGroupDoc.data().vehicles.filter((id: string) => id !== vehicle.id),
      });
    }
  }

  getExportData() {
    const vehicles = this.vehicles().map((vehicle) => vehicle as Vehicle);
    const columns = [
      { name: 'Naam', itemProperty: 'name' },
      { name: 'Actief', itemProperty: 'active' },
      { name: 'Zitplaatsen', itemProperty: 'seats' },
      { name: 'Volgordenummer', itemProperty: 'calendarStyle.sortingNumber' },
      { name: 'Kilometerstand', itemProperty: 'currentMileage' },
      { name: 'Auto kleur', itemProperty: 'calendarStyle.color' },
      { name: 'Rit leeg kleur', itemProperty: 'calendarStyle.backgroundColorEmpty' },
      { name: 'Rit vol kleur', itemProperty: 'calendarStyle.backgroundColorFull' },
      { name: 'Geen chauffeur kleur', itemProperty: 'calendarStyle.backgroundColorNoDriver' },
    ];
    return { items: vehicles, columns: columns, options: { sortBy: 'Naam' } };
  }

  getImportConfig() {
    const columnSettings = [
      { columnName: 'naam', itemProperty: 'name', required: true },
      { columnName: 'actief', itemProperty: 'active', type: 'boolean', defaultValue: false },
      {
        columnName: 'zitplaatsen',
        itemProperty: 'seats',
        type: 'number',
        defaultValue: 0,
        min: 0,
      },
      {
        columnName: 'volgordenummer',
        itemProperty: 'calendarStyle.sortingNumber',
        type: 'number',
        defaultValue: null,
        min: 0,
      },
      {
        columnName: 'kilometerstand',
        itemProperty: 'currentMileage',
        type: 'number',
        defaultValue: 0,
        min: 0,
      },
      {
        columnName: 'auto kleur',
        itemProperty: 'calendarStyle.color',
        defaultValue: '#efefef',
      },
      {
        columnName: 'rit leeg kleur',
        itemProperty: 'calendarStyle.backgroundColorEmpty',
        defaultValue: '#efefef',
      },
      {
        columnName: 'rit vol kleur',
        itemProperty: 'calendarStyle.backgroundColorFull',
        defaultValue: '#efefef',
      },
      {
        columnName: 'geen chauffeur kleur',
        itemProperty: 'calendarStyle.backgroundColorNoDriver',
        defaultValue: '#efefef',
      },
    ];
    const existingData = this.vehicles().map((vehicle) => vehicle as Vehicle);

    return {
      tab: 'vehicles',
      uniqueKey: 'name',
      organisationId: this.organisationId,
      columnSettings: columnSettings,
      existingData: existingData,
    };
  }
}
